import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import TodayIcon from '@mui/icons-material/Today';
import {
  Toolbar,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  Typography,
  Box,
  Stack,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { isEmpty, isNil } from 'lodash';
import type { CalendarView } from './CalendarPage';
import type { Dayjs } from 'dayjs';
import type { ReactElement, FC } from 'react';

interface DateRange {
  start: Date;
  end: Date;
}

/**
 * Устанавливает время в дате на начало дня (00:00:00)
 *
 * @param date - дата, для которой нужно установить время
 * @returns {Date} - новая дата с установленным временем на начало дня
 */
const setStartOfDay = (date: Date): Date => {
  const result = new Date(date);
  result.setHours(0, 0, 0, 0);
  return result;
};

/**
 * Устанавливает время в дате на конец дня (23:59:59.999)
 *
 * @param date - дата, для которой нужно установить конец дня
 * @returns {Date} - новая дата с установленным временем на конец дня
 */
const setEndOfDay = (date: Date): Date => {
  const result = new Date(date);
  result.setHours(23, 59, 59, 999);
  return result;
};

/**
 * Получает диапазон дат для месячного представления
 *
 * @param date - дата, для которой нужно получить диапазон
 * @returns {DateRange} - объект с начальной и конечной датами диапазона
 */
const getMonthRange = (date: Date): DateRange => {
  const start = setStartOfDay(new Date(date.getFullYear(), date.getMonth(), 1));
  const end = setEndOfDay(new Date(date.getFullYear(), date.getMonth() + 1, 0));
  return { start, end };
};

const CalendarHeader: FC<CalendarHeaderProps> = ({
  title,
  view,
  startDate,
  endDate,
  onDateRangeChange,
  onViewChange,
}): ReactElement => {
  const handleListViewDateChange = (type: 'start' | 'end', value: Dayjs | null): void => {
    if (isNil(value)) return;

    const date = value.toDate();
    if (type === 'start') {
      onDateRangeChange({
        start: setStartOfDay(date),
        end: endDate,
      });
    } else {
      onDateRangeChange({
        start: startDate,
        end: setEndOfDay(date),
      });
    }
  };

  const handleGridViewDateChange = (direction: 'prev' | 'next' | 'today'): void => {
    const newDate = new Date(startDate);
    if (direction === 'prev') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else if (direction === 'next') {
      newDate.setMonth(newDate.getMonth() + 1);
    } else {
      newDate.setTime(new Date().getTime());
    }
    onDateRangeChange(getMonthRange(newDate));
  };

  const handleViewChange = (newView: CalendarView): void => {
    onViewChange(newView);
  };

  return (
    <Toolbar>
      <Stack direction='row' spacing={2} alignItems='center'>
        {!isEmpty(title) && (
          <Typography component='div' variant='button'>
            {title}
          </Typography>
        )}

        {view === 'monthlyList' ? (
          <Stack direction='row' spacing={2} alignItems='center'>
            <DatePicker
              label='От'
              value={dayjs(startDate)}
              onChange={(value) => {
                handleListViewDateChange('start', value);
              }}
              slotProps={{
                textField: {
                  size: 'small',
                },
              }}
            />
            <DatePicker
              label='До'
              value={dayjs(endDate)}
              onChange={(value) => {
                handleListViewDateChange('end', value);
              }}
              minDate={dayjs(startDate)}
              slotProps={{
                textField: {
                  size: 'small',
                },
              }}
            />
          </Stack>
        ) : (
          <>
            <Tooltip title='Предыдущий период' aria-label='Перейти к предыдущему периоду'>
              <IconButton
                onClick={() => {
                  handleGridViewDateChange('prev');
                }}>
                <NavigateBeforeIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title='Следующий период' aria-label='Перейти к следующему периоду'>
              <IconButton
                onClick={() => {
                  handleGridViewDateChange('next');
                }}>
                <NavigateNextIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title='Сегодня' aria-label='Перейти к текущей дате'>
              <IconButton
                onClick={() => {
                  handleGridViewDateChange('today');
                }}>
                <TodayIcon />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Stack>

      <Box flexGrow={1} mr={2} />

      <Select
        value={view}
        size='small'
        aria-label='Выбор вида календаря'
        onChange={(e) => {
          handleViewChange(e.target.value as CalendarView);
        }}>
        <MenuItem value='dayGridMonth'>Месяц</MenuItem>
        <MenuItem value='monthlyList'>Дни</MenuItem>
      </Select>
    </Toolbar>
  );
};

export interface CalendarHeaderProps {
  title: string;
  view: CalendarView;
  startDate: Date;
  endDate: Date;
  onDateRangeChange: (range: DateRange) => void;
  onViewChange: (viewName: CalendarView) => void;
}

export type { DateRange };
export default CalendarHeader;
